import {
  CompanyCreditProfile,
  CompanyRole,
  RegulatoryStatus,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { AvailableUserRoles } from './referenceApi';
import { ApiTags, CommonTagIds, CompanyTagIds } from './tags';
import { UserFull } from './userApi';

export type CompanyBasic = {
  id: string;
  name: string;
  companyRoleId: CompanyRole;
  companyRoleName: string;
  regulatoryStatusId: RegulatoryStatus;
  regulatoryStatusName: string;
  nonSefParticipantAgreementConsentDate: string;
  sefParticipantAgreementConsentDate: string;
  isSpvFlag: boolean;
  parentCompanyId: string;
  parentCompanyName: string;
  lei: string;
  created: string;
};

export type Company = CompanyBasic & {
  phoneNumber: string;
  addressStreet: string;
  addressZipCode: string;
  cityName: string;
  stateName: string;
  stateId: number;
  countryName: string;
  countryId: number;
  isSefParticipantAgreementConsent: boolean;
  manualRegulatoryStatus: string;
  isFederalEntityIndicator: boolean;
  isFinancialEntityIndicator: boolean;
  description: string;
  creditProfileId: CompanyCreditProfile;
  creditProfileName: string;
  isRfqNegotiationFlag: boolean;
  isAutoExecutionFlag: boolean;
};

export type CompanySearchResult = {
  id: string;
  name: string;
};

export type CompanySearchParams = {
  companyRoles?: CompanyRole[];
  excludeSpv?: boolean;
  excludeCompanyId?: string;
};

export type CompanyMemberRole = { id: string; name: string };

export type CompanyMember = Omit<
  UserFull,
  'id' | 'primaryCompanyId' | 'primaryCompanyName'
> & {
  membershipId: string;
  companyId: string;
  roles: CompanyMemberRole[];
  userId: string;
};

export type AssignableUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type LegalEntityDto = {
  id: string;
  name: string;
};

export type CreateCompanyRequest = {
  parentCompanyId?: string;
  name: string;
  lei: string;
  phoneNumber: string;
  cityName: string;
  stateId: number;
  countryId: number;
  addressStreet: string;
  addressZipCode: string;
  companyRole: CompanyRole;
  sefParticipantAgreementConsent: boolean;
  sefParticipantAgreementConsentDate?: string;
  nonSefParticipantAgreementConsentDate?: string;
  regulatoryStatus: RegulatoryStatus;
  manualRegulatoryStatus?: string;
  federalEntityIndicator: boolean;
  financialEntityIndicator: boolean;
  isSpvFlag: boolean;
  creditProfile: CompanyCreditProfile;
  rfqNegotiationFlag: boolean;
  autoExecutionFlag: boolean;
  description?: string;
};

export type LegalEntity = {
  id: string;
  name: string;
};

const BASE_URL = '/api/v1/companies';

export const companyApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getCompanies: build.query<CompanyBasic[], void>({
      query: () => BASE_URL,
      providesTags: [{ type: ApiTags.COMPANIES, id: CommonTagIds.LIST }],
    }),
    getCompanyById: build.query<Company, string>({
      query: companyId => `${BASE_URL}/${companyId}`,
      providesTags: (result, error, companyId) => [
        { type: ApiTags.COMPANIES, id: companyId },
      ],
    }),
    searchCompanies: build.query<CompanySearchResult[], CompanySearchParams>({
      query: params => {
        const companyRoles = params.companyRoles || [];
        const searchParams = new URLSearchParams();

        // BE expects array params like this: endpoint?param=val1&param=val2 (no brackets)
        companyRoles.forEach(role => searchParams.append('companyRoles', role));
        if (params.excludeCompanyId) {
          searchParams.append('excludeCompanyId', params.excludeCompanyId);
        }
        searchParams.append('excludeSpv', String(!!params.excludeSpv));

        return `${BASE_URL}/search?${searchParams.toString()}`;
      },
      providesTags: [{ type: ApiTags.COMPANIES, id: CompanyTagIds.SEARCH }],
    }),
    getCompanyMemberships: build.query<CompanyMember[], string>({
      query: companyId => `${BASE_URL}/${companyId}/memberships`,
      providesTags: (result, error, companyId) => [
        {
          type: ApiTags.COMPANIES,
          id: `${companyId}-${CompanyTagIds.MEMBERSHIPS}`,
        },
      ],
    }),
    getCompanyAssignableUsers: build.query<AssignableUser[], string>({
      query: companyId => `${BASE_URL}/${companyId}/assignable-users`,
      providesTags: (result, error, companyId) => [
        {
          type: ApiTags.COMPANIES,
          id: `${companyId}-${CompanyTagIds.ASSIGNABLE}`,
        },
      ],
    }),
    getCompanyUserRoles: build.query<AvailableUserRoles, string>({
      query: companyId => `${BASE_URL}/${companyId}/user-roles`,
      providesTags: (result, error, companyId) => [
        { type: ApiTags.COMPANIES, id: `${companyId}-${CompanyTagIds.ROLES}` },
      ],
    }),
    getAssignableAdvisorsByAdvisorCompanyId: build.query<
      AssignableUser[],
      { companyId: string; advisorCompanyId: string }
    >({
      query: ({ companyId, advisorCompanyId }) =>
        `${BASE_URL}/${companyId}/assignable-advisors?advisorCompanyId=${advisorCompanyId}`,
      providesTags: (result, error, { companyId, advisorCompanyId }) => [
        {
          type: ApiTags.COMPANIES,
          id: `${companyId}-${advisorCompanyId}-${CompanyTagIds.ASSIGNABLE}`,
        },
      ],
    }),
    getCompanyLegalEntities: build.query<LegalEntityDto[], string>({
      query: companyId => `${BASE_URL}/${companyId}/legal-entities`,
      providesTags: (result, error, companyId) => [
        {
          type: ApiTags.COMPANIES,
          id: `${companyId}-${CompanyTagIds.LEGAL_ENTITIES}`,
        },
      ],
    }),
    // The offer form's Contract Legal Entity options: the same parent and SPV set, narrowed by the
    // backend to the companies where the user may create offers. The project form keeps the
    // unfiltered list above.
    getCompanyContractLegalEntities: build.query<LegalEntityDto[], string>({
      query: companyId => `${BASE_URL}/${companyId}/contract-legal-entities`,
      providesTags: (result, error, companyId) => [
        {
          type: ApiTags.COMPANIES,
          id: `${companyId}-${CompanyTagIds.CONTRACT_LEGAL_ENTITIES}`,
        },
      ],
    }),
    createCompany: build.mutation<Company, CreateCompanyRequest>({
      query: data => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: ApiTags.COMPANIES, id: CommonTagIds.LIST }],
    }),
    updateCompany: build.mutation<
      Company,
      { id: string; data: CreateCompanyRequest }
    >({
      query: ({ id, data }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: ApiTags.COMPANIES, id },
        { type: ApiTags.COMPANIES, id: CommonTagIds.LIST },
        { type: ApiTags.COMPANIES, id: CompanyTagIds.SEARCH },
        { type: ApiTags.COMPANIES, id: `${id}-${CompanyTagIds.ROLES}` },
      ],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useSearchCompaniesQuery,
  useGetCompanyMembershipsQuery,
  useGetCompanyAssignableUsersQuery,
  useGetCompanyUserRolesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useGetAssignableAdvisorsByAdvisorCompanyIdQuery,
  useGetCompanyLegalEntitiesQuery,
  useGetCompanyContractLegalEntitiesQuery,
} = companyApi;
